'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Globe } from 'lucide-react';
import { useLocale } from '@/components/LocaleProvider';

const LANGUAGES = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
] as const;

interface LanguageSwitcherProps {
  variant?: 'desktop' | 'mobile';
  dark?: boolean;
  transparent?: boolean; // navbar transparent on hero (white text)
}

export default function LanguageSwitcher({ variant = 'desktop', dark = false, transparent = false }: LanguageSwitcherProps) {
  const { locale: current, setLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleChange = (code: string) => {
    setLocale(code);
    setOpen(false);
  };

  const currentLang = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  if (variant === 'mobile') {
    return (
      <div className="flex gap-1.5">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            onClick={() => handleChange(lang.code)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
              current === lang.code
                ? dark
                  ? 'bg-[rgba(96,165,250,0.15)] text-[#60A5FA] border border-[rgba(96,165,250,0.3)]'
                  : 'bg-blue-50 text-blue-700 border border-blue-200'
                : dark
                  ? 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)] border border-transparent'
                  : 'text-slate-500 hover:bg-slate-50 border border-transparent'
            }`}
          >
            <span className="text-sm">{lang.flag}</span>
            {lang.code.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-bold transition-all border ${
          dark
            ? 'bg-[rgba(255,255,255,0.06)] text-[#9BA7B4] border-[rgba(255,255,255,0.1)] hover:border-[rgba(96,165,250,0.3)] hover:text-[#F4F6F8]'
            : transparent
              ? 'bg-white/10 text-white/80 border-white/20 hover:bg-white/20 hover:text-white backdrop-blur-md'
              : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200 hover:text-slate-800'
        }`}
        aria-label="Change language"
      >
        <Globe className="w-4 h-4" />
        <span className="text-xs tracking-wider">{currentLang.code.toUpperCase()}</span>
      </button>

      {open && (
        <div className={`absolute top-full right-0 mt-2 w-44 rounded-2xl p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150 ${
          dark
            ? 'bg-[#1B3254] border border-[rgba(96,165,250,0.2)] shadow-[0_15px_40px_rgba(0,0,0,0.5)]'
            : 'bg-white shadow-[0_15px_40px_rgba(0,0,0,0.15)] border border-slate-100'
        }`}>
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleChange(lang.code)}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                current === lang.code
                  ? dark
                    ? 'bg-[rgba(96,165,250,0.12)] text-[#60A5FA]'
                    : 'bg-blue-50 text-blue-700'
                  : dark
                    ? 'text-[#9BA7B4] hover:bg-[rgba(255,255,255,0.06)] hover:text-[#F4F6F8]'
                    : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span className="text-base">{lang.flag}</span>
              <span>{lang.label}</span>
              {current === lang.code && (
                <span className={`ml-auto w-2 h-2 rounded-full ${dark ? 'bg-[#60A5FA]' : 'bg-blue-600'}`} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
